import React from "react";
import { getAliasMatches, getMatchDays, getMatches, getMatchesBetween, MatchViewModel } from "../matches/MatchesStore";
import { MatchRow } from "../matches/MatchRow";
import { Box, Button, Calendar, DataTable, DropButton, Grid, Heading, List, ResponsiveContext, Stack, Tab, Tabs, Text } from "grommet";

import { format, parseJSON, formatRelative, isSameDay, parse, startOfDay, endOfDay } from "date-fns";
import { AliasOverview, AliasViewModel, getAliasesForUser, getAliasOverview } from "../aliases/AliasStore";
import { usePermission, useUser } from "../account/AccountStore";
import { TeamBlock } from "../teams/TeamsManagement";
import { RenderStat, StatValue } from "../stats/StatRenderer";
import { Stat, StatIcon } from "../stats/StatIcon";
import { orderBy, over } from "lodash";
import ScrollContainer from "../components/ScrollContainer";
import { SmallMatchRow } from "../matches/SmallMatchRow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormatDateTime } from "../components/DateTimePicker";
import StyledModal from "../components/StyledModel";
import SkeletonWrapper from "../components/SkeletonWrapper";
import { TeamCard } from "../teams/TeamCard";
import CustomButton from "../components/CustomButton";
import MatchCalendar from "../matches/MatchCalendar";

const dateFormat = "yyyy-MM-dd";

const UserHome = () => {
    const user = useUser();
    const [upcomingMatches, setUpcomingMatches] = React.useState([] as MatchViewModel[]);
    const [pastMatches, setPastMatches] = React.useState([] as MatchViewModel[]);
    const [matches, setMatches] = React.useState([] as MatchViewModel[]);

    const [showOverview, setShowOverview] = React.useState(false);
    const [overview, setOverview] = React.useState({} as AliasOverview);
    const [matchDays, setMatchDays] = React.useState([] as string[]);

    const [aliases, setAliases] = React.useState([] as AliasViewModel[]);

    const hasManagementPermission = usePermission("sport:management");

    React.useEffect(() => {
        if (user.resolved) {
            getMatches(true, false, false, 0, 10).then((_matches) => {
                setUpcomingMatches([...upcomingMatches, ..._matches]);
            });
            getMatches(false, true, false, 0, 10).then((_matches) => {
                setPastMatches([...upcomingMatches, ..._matches]);
            });
            getAliasesForUser(user.id).then((aliases) => {
                setAliases(aliases);
                if (aliases.length > 0) {
                    getAliasOverview(aliases[0].id).then((res) => {
                        setOverview(res.result);
                        setShowOverview(true);
                    });
                }
            });

            getMatchDays().then((days) => {
                setMatchDays(days);
            });
        }
    }, [user]);

    const [showLoadMoreUpcoming, setShowLoadMoreUpcoming] = React.useState(true);

    const loadMoreUpcomingMatches = () => {
        getMatches(true, false, false, upcomingMatches.length, 10).then((_matches) => {
            setUpcomingMatches([...upcomingMatches, ..._matches]);
            setShowLoadMoreUpcoming(_matches.length >= 10);
        });
    };

    const [showLoadMorePast, setShowLoadMorePast] = React.useState(true);

    const loadMorePastMatches = () => {
        getMatches(false, true, false, pastMatches.length, 10).then((_matches) => {
            setPastMatches([...pastMatches, ..._matches]);
            setShowLoadMorePast(_matches.length >= 10);
        });
    };

    const size = React.useContext(ResponsiveContext);

    const [selectedDay, setSelectedDay] = React.useState(FormatDateTime(new Date(), dateFormat));

    const [showScheduleModal, setShowScheduleModal] = React.useState(false);

    const [scheduleLoading, setScheduleLoading] = React.useState(false);

    const showSchedule = async (selectedDay) => {
        setSelectedDay(selectedDay);
        setScheduleLoading(true);

        let day = parse(selectedDay, dateFormat, new Date());

        let matches = await getMatchesBetween(startOfDay(day), endOfDay(day));
        setMatches(matches);
        setScheduleLoading(false);
        if (matches.length != 0) {
            setShowScheduleModal(true);
        }
    };

    const [matchesTabIndex, setMatchesTabIndex] = React.useState(0);

    return (
        <Box align="center" justify="center" fill pad="small">
            <Grid columns={size == "medium" || size == "small" ? { size: "auto", count: 1 } : { size: "auto", count: aliases.length > 0 ? 3 : 2 }} gap="medium">
                <Box>
                    <Box direction="row" gap="small">
                        <CustomButton background={matchesTabIndex == 0 ? "background" : ""} onClick={() => setMatchesTabIndex(0)} label="Upcoming Matches" fill></CustomButton>
                        <CustomButton background={matchesTabIndex == 1 ? "background" : ""} onClick={() => setMatchesTabIndex(1)} label="Past Matches" fill></CustomButton>
                    </Box>
                    <Tabs activeIndex={matchesTabIndex} onActive={setMatchesTabIndex}>
                        <Tab>
                            <ScrollContainer direction={"-y"} style={{ maxHeight: "610px" }}>
                                <Box gap="small">
                                    {upcomingMatches.map((match) => (
                                        <SmallMatchRow key={match.id} match={match}></SmallMatchRow>
                                    ))}
                                </Box>
                            </ScrollContainer>
                            {showLoadMoreUpcoming && <CustomButton primary label="Load more" size="small" style={{ borderRadius: "5px", marginTop: "5px" }} onClick={loadMoreUpcomingMatches}></CustomButton>}
                        </Tab>
                        <Tab>
                            <ScrollContainer direction={"-y"} style={{ maxHeight: "610px" }}>
                                <Box gap="small">
                                    {pastMatches.map((match) => (
                                        <SmallMatchRow key={match.id} match={match}></SmallMatchRow>
                                    ))}
                                </Box>
                            </ScrollContainer>
                            {showLoadMorePast && <CustomButton primary label="Load more" size="small" style={{ borderRadius: "5px", marginTop: "5px" }} onClick={loadMorePastMatches}></CustomButton>}
                        </Tab>
                    </Tabs>
                </Box>

                {showOverview && (
                    <Box gap="medium">
                        <Box direction="row" gap="small">
                            {overview.matchesPlayed !== undefined && (
                                <Box pad="medium" align="center" justify="center" background={"background"} round="5px" fill>
                                    <Text size="xlarge">
                                        <FontAwesomeIcon icon={["fad", "swords"]}></FontAwesomeIcon> {overview.matchesPlayed}
                                    </Text>
                                    <Text>Matches Played</Text>
                                </Box>
                            )}
                            {overview.matchesWon !== undefined && (
                                <Box pad="medium" align="center" justify="center" background={"background"} round="5px" fill>
                                    <Text size="xlarge">
                                        {overview.matchesWon} <FontAwesomeIcon icon={["fad", "trophy"]}></FontAwesomeIcon>
                                    </Text>
                                    <Text>Matches Won</Text>
                                </Box>
                            )}
                        </Box>
                        {overview.stats && (
                            <>
                                <Box direction="row" gap="small" wrap align="center" justify="center">
                                    {overview.stats.map((stat) => {
                                        return (
                                            <Box key={stat.id} pad="small" align="center" justify="center" background={"background"} round="5px" fill>
                                                <Text size="xlarge">
                                                    {stat.value} {StatIcon(stat.stat)}
                                                </Text>
                                                <Text>{stat.stat.name}</Text>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </>
                        )}
                        {overview.teams && (
                            <Box direction="row" gap="small" wrap align="center" justify="center">
                                {overview.teams?.map((team) => (
                                    <TeamCard key={team.id} team={team} width={150}></TeamCard>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
                <Box direction="row" justify="center">
                    <Box>
                        <MatchCalendar matchDays={matchDays} selectedDay={selectedDay} onSelect={showSchedule}></MatchCalendar>
                    </Box>
                </Box>
            </Grid>
            <StyledModal isOpen={showScheduleModal} setIsOpen={() => setShowScheduleModal(false)}>
                <Box width={"large"} gap="small">
                    <Heading level={3}>Matches on {selectedDay}</Heading>
                    <SkeletonWrapper loading={scheduleLoading}>
                        <Box gap="small">
                            {matches.map((match: MatchViewModel) => (
                                <Box key={"s" + match.id}>
                                    <SmallMatchRow match={match}></SmallMatchRow>
                                </Box>
                            ))}
                        </Box>
                    </SkeletonWrapper>
                    <CustomButton primary label="Close" onClick={() => setShowScheduleModal(false)}></CustomButton>
                </Box>
            </StyledModal>
        </Box>
    );
};

export default UserHome;
